// Page editor — ARRANGE ONLY (motiflink §2.4, M4 design note §8).
//
// This screen composes; it does not author. Copy lives one click away in the
// block editor, which is where the live preview is. Keeping the two apart is
// what lets the arrange view stay a scannable list of a whole page rather than
// ten expanded forms stacked vertically.
//
// What it owns: page-level fields (AR/EN), block order, block visibility,
// add/delete, and the snapshot panel that replaced draft/publish now that
// marketing is SSR and every edit is immediately live.

import type { Locale } from '@fajrarabi/i18n/locales';
import {
  Alert,
  BilingualTabs,
  Badge,
  Button,
  Dialog,
  DialogContent,
  Input,
  Textarea,
} from '@fajrarabi/ui';
import type { BilingualValue } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '~/lib/api';
import { marketingOrigin } from '~/lib/block-preview';
import { BLOCK_GALLERY, BLOCK_SCHEMAS, blockLabel, emptyContentFor } from '~/lib/block-schemas';
import {
  createBlock,
  createSnapshot,
  deleteBlock,
  deletePage,
  deleteSnapshot,
  getPage,
  listSnapshots,
  reorderBlocks,
  restoreSnapshot,
  updateBlock,
  updatePage,
  type BlockSummary,
  type PageDetail,
  type TranslationOverlay,
} from '~/lib/pages';

export const Route = createFileRoute('/_authed/pages/$id')({
  component: PageEditorRoute,
});

function PageEditorRoute() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const pageQuery = useQuery({
    queryKey: ['pages', 'detail', id],
    queryFn: ({ signal }) => getPage(id, signal),
    staleTime: 2_000,
  });

  if (pageQuery.isLoading) {
    return <p className="py-16 text-center text-sm text-admin-500">Loading…</p>;
  }
  if (pageQuery.error || !pageQuery.data) {
    return (
      <div className="mx-auto max-w-2xl py-10">
        <Alert tone="danger">Could not load this page.</Alert>
        <Button variant="ghost" className="mt-4" onClick={() => void navigate({ to: '/pages' })}>
          ← All pages
        </Button>
      </div>
    );
  }

  return <PageEditor page={pageQuery.data.page} blocks={pageQuery.data.blocks} />;
}

function PageEditor({ page, blocks }: { page: PageDetail; blocks: BlockSummary[] }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gallery, setGallery] = useState(false);
  const [confirmDeleteBlock, setConfirmDeleteBlock] = useState<BlockSummary | null>(null);
  const [confirmDeletePage, setConfirmDeletePage] = useState(false);

  // Local order so a reorder repaints instantly; the server reindexes 0..n-1
  // and the refetch reconciles.
  const [order, setOrder] = useState<string[]>(() => blocks.map((b) => b.id));
  useEffect(() => setOrder(blocks.map((b) => b.id)), [blocks]);

  const byId = useMemo(() => new Map(blocks.map((b) => [b.id, b])), [blocks]);
  const ordered = order.flatMap((id) => {
    const block = byId.get(id);
    return block ? [block] : [];
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['pages', 'detail', page.id] });
    void qc.invalidateQueries({ queryKey: ['pages', 'list'] });
  };
  const flash = (message: string) => {
    setNotice(message);
    setError(null);
    window.setTimeout(() => setNotice(null), 2500);
  };
  const onError = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Something went wrong.');

  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderBlocks(page.id, ids),
    onSuccess: invalidate,
    onError: (err) => {
      // Roll the optimistic order back to the server's.
      setOrder(blocks.map((b) => b.id));
      onError(err);
    },
  });

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = order.slice();
    const [moved] = next.splice(index, 1);
    if (moved === undefined) return;
    next.splice(target, 0, moved);
    setOrder(next);
    reorder.mutate(next);
  }

  const addBlock = useMutation({
    mutationFn: (type: (typeof BLOCK_GALLERY)[number]) =>
      createBlock(page.id, {
        type,
        content: emptyContentFor(type),
        settings: {},
        translations: {},
        isActive: true,
      }),
    onSuccess: ({ id }) => {
      setGallery(false);
      invalidate();
      void navigate({
        to: '/pages/$id/blocks/$blockId',
        params: { id: page.id, blockId: id },
      });
    },
    onError: (err) => {
      setGallery(false);
      onError(err);
    },
  });

  const toggleBlock = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateBlock(page.id, id, { isActive }),
    onSuccess: invalidate,
    onError,
  });

  const removeBlock = useMutation({
    mutationFn: (id: string) => deleteBlock(page.id, id),
    onSuccess: () => {
      setConfirmDeleteBlock(null);
      flash('Block deleted.');
      invalidate();
    },
    onError,
  });

  const removePage = useMutation({
    mutationFn: () => deletePage(page.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pages', 'list'] });
      void navigate({ to: '/pages' });
    },
    onError,
  });

  const liveUrl = `${marketingOrigin().replace(/\/+$/, '')}/${page.slug}`;

  return (
    <div className="mx-auto max-w-5xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => void navigate({ to: '/pages' })}
            className="text-sm font-medium text-admin-600 transition-colors hover:text-admin-900"
          >
            ← All pages
          </button>
          <h1 className="mt-2 truncate text-2xl font-semibold text-admin-900">
            {page.title ?? (page.isHomepage ? 'Home' : `/${page.slug}`)}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-admin-500">
            <span className="font-mono text-xs">{page.slug === '' ? '/' : `/${page.slug}`}</span>
            <a
              href={liveUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand-600 hover:underline"
            >
              View live ↗
            </a>
          </p>
        </div>
        <Badge tone={page.isActive ? 'success' : 'neutral'}>
          {page.isActive ? 'Live' : 'Hidden'}
        </Badge>
      </header>

      {notice && (
        <p role="status" className="mt-4 rounded-xl bg-leaf/10 px-4 py-3 text-sm text-leaf">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 rounded-xl bg-heart/10 px-4 py-3 text-sm text-heart">
          {error}
        </p>
      )}

      <PageDetailsCard
        page={page}
        onSaved={(m) => {
          flash(m);
          invalidate();
        }}
        onError={onError}
      />

      {/* ── Blocks ── */}
      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-admin-900">Blocks</h2>
            <p className="mt-0.5 text-sm text-admin-500">
              Top to bottom, as they appear on the page. Click a block to edit its copy.
            </p>
          </div>
          <Button onClick={() => setGallery(true)}>Add block</Button>
        </div>

        {ordered.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-admin-200 bg-white px-6 py-12 text-center text-sm text-admin-500">
            This page has no blocks yet. Add one to start composing it.
          </p>
        ) : (
          <ol className="mt-4 space-y-2">
            {ordered.map((block, index) => (
              <BlockRow
                key={block.id}
                pageId={page.id}
                block={block}
                index={index}
                total={ordered.length}
                busy={reorder.isPending}
                onMove={(delta) => move(index, delta)}
                onToggle={() => toggleBlock.mutate({ id: block.id, isActive: !block.isActive })}
                onDelete={() => setConfirmDeleteBlock(block)}
              />
            ))}
          </ol>
        )}
      </section>

      <SnapshotsPanel pageId={page.id} onFlash={flash} onError={onError} onRestored={invalidate} />

      {/* ── Danger zone ── */}
      <section className="mt-8 rounded-2xl border border-heart/20 bg-white p-5">
        <h2 className="text-sm font-semibold text-admin-900">Delete this page</h2>
        <p className="mt-1 text-sm text-admin-500">
          Removes the page and every block on it. Take a snapshot first if you might want it back.
        </p>
        <Button variant="ghost" className="mt-3" onClick={() => setConfirmDeletePage(true)}>
          Delete page
        </Button>
      </section>

      {gallery && (
        <BlockGalleryDialog
          pending={addBlock.isPending}
          onPick={(type) => addBlock.mutate(type)}
          onClose={() => setGallery(false)}
        />
      )}

      {confirmDeleteBlock && (
        <ConfirmDialog
          title="Delete this block?"
          body={
            <>
              <span className="font-semibold">{blockLabel(confirmDeleteBlock.type)}</span> and its
              copy in both languages are removed from this page. This is not undoable except by
              restoring a snapshot.
            </>
          }
          confirmLabel="Delete block"
          destructive
          pending={removeBlock.isPending}
          onCancel={() => setConfirmDeleteBlock(null)}
          onConfirm={() => removeBlock.mutate(confirmDeleteBlock.id)}
        />
      )}

      {confirmDeletePage && (
        <ConfirmDialog
          title="Delete this page?"
          body={
            <>
              <span className="font-mono text-xs">{page.slug === '' ? '/' : `/${page.slug}`}</span>{' '}
              and all {blocks.length} of its blocks will be removed, and the URL will start
              returning 404.
            </>
          }
          confirmLabel="Delete page"
          destructive
          pending={removePage.isPending}
          onCancel={() => setConfirmDeletePage(false)}
          onConfirm={() => removePage.mutate()}
        />
      )}
    </div>
  );
}

// ─── Page-level fields ──────────────────────────────────────────────────────

const OVERLAY_LOCALE = 'en';

/** Project `content`-side value + `translations.en` into the `{ar, en}` shape. */
function bilingualOf(
  base: string | null,
  translations: TranslationOverlay,
  key: string,
): BilingualValue {
  const en = translations[OVERLAY_LOCALE]?.[key];
  const value: Partial<Record<Locale, string>> = {};
  if (base) value.ar = base;
  if (typeof en === 'string' && en !== '') value.en = en;
  return Object.keys(value).length === 0 ? null : value;
}

function PageDetailsCard({
  page,
  onSaved,
  onError,
}: {
  page: PageDetail;
  onSaved: (message: string) => void;
  onError: (err: unknown) => void;
}) {
  const [slug, setSlug] = useState(page.slug);
  const [isActive, setIsActive] = useState(page.isActive);
  const [title, setTitle] = useState(page.title ?? '');
  const [metaTitle, setMetaTitle] = useState(page.metaTitle ?? '');
  const [metaDescription, setMetaDescription] = useState(page.metaDescription ?? '');
  const [overlay, setOverlay] = useState<Record<string, unknown>>(
    () => page.translations[OVERLAY_LOCALE] ?? {},
  );

  const baseline = JSON.stringify({
    slug: page.slug,
    isActive: page.isActive,
    title: page.title ?? '',
    metaTitle: page.metaTitle ?? '',
    metaDescription: page.metaDescription ?? '',
    overlay: page.translations[OVERLAY_LOCALE] ?? {},
  });
  const dirty =
    JSON.stringify({ slug, isActive, title, metaTitle, metaDescription, overlay }) !== baseline;

  const save = useMutation({
    mutationFn: () =>
      updatePage(page.id, {
        slug: slug.trim().replace(/^\/+/, ''),
        isActive,
        title: title.trim() || null,
        metaTitle: metaTitle.trim() || null,
        metaDescription: metaDescription.trim() || null,
        translations: Object.keys(overlay).length > 0 ? { [OVERLAY_LOCALE]: overlay } : {},
      }),
    onSuccess: () => onSaved('Page details saved.'),
    onError,
  });

  /** AR writes the base column; EN writes (or clears) the overlay key. */
  function write(
    key: 'title' | 'metaTitle' | 'metaDescription',
    setBase: (next: string) => void,
    locale: Locale,
    next: string,
  ) {
    if (locale === 'ar') {
      setBase(next);
      return;
    }
    setOverlay((prev) => {
      const copy = { ...prev };
      if (next.trim() === '') delete copy[key];
      else copy[key] = next;
      return copy;
    });
  }

  return (
    <section className="mt-6 rounded-2xl border border-admin-200 bg-white p-5">
      <h2 className="text-lg font-semibold text-admin-900">Page details</h2>
      <p className="mt-0.5 text-sm text-admin-500">
        The Arabic tab is the base value; English overrides it. Blank English falls through to
        Arabic rather than rendering empty.
      </p>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <label className="block">
          <span className="text-sm font-semibold text-admin-900">Slug</span>
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="mt-1.5 font-mono text-sm"
            placeholder="about"
          />
          <span className="mt-1 block text-xs text-admin-500">
            Empty = the home page. Changing this changes the live URL.
          </span>
        </label>

        <div className="flex items-start gap-3 md:pt-7">
          <input
            id="page-active"
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-admin-300 text-brand-500 focus:ring-brand-500"
          />
          <label htmlFor="page-active" className="text-sm">
            <span className="font-semibold text-admin-900">Live on the site</span>
            <span className="mt-0.5 block text-xs text-admin-500">
              Switch off to hide the page without deleting it — the URL 404s.
            </span>
          </label>
        </div>

        <BilingualPageField
          label="Page title"
          help="Used as the <h1> fallback and the browser tab title when no meta title is set."
          value={bilingualOf(title || null, { [OVERLAY_LOCALE]: overlay }, 'title')}
          onChange={(locale, next) => write('title', setTitle, locale, next)}
        />

        <BilingualPageField
          label="Meta title"
          help="Search-result headline. Around 60 characters."
          value={bilingualOf(metaTitle || null, { [OVERLAY_LOCALE]: overlay }, 'metaTitle')}
          onChange={(locale, next) => write('metaTitle', setMetaTitle, locale, next)}
        />

        <div className="md:col-span-2">
          <BilingualPageField
            label="Meta description"
            help="Search-result snippet. Around 155 characters."
            multiline
            value={bilingualOf(
              metaDescription || null,
              { [OVERLAY_LOCALE]: overlay },
              'metaDescription',
            )}
            onChange={(locale, next) => write('metaDescription', setMetaDescription, locale, next)}
          />
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>
          Save details
        </Button>
      </div>
    </section>
  );
}

function BilingualPageField({
  label,
  help,
  value,
  onChange,
  multiline,
}: {
  label: string;
  help: string;
  value: BilingualValue;
  onChange: (locale: Locale, next: string) => void;
  multiline?: boolean;
}) {
  return (
    <div>
      <span className="text-sm font-semibold text-admin-900">{label}</span>
      <p className="mt-0.5 text-xs text-admin-500">{help}</p>
      <BilingualTabs
        className="mt-2"
        value={value}
        ariaLabel={`${label} language`}
        onChange={onChange}
      >
        {({ value: localeValue, dir, id, onChange: setValue }) =>
          multiline ? (
            <Textarea
              id={id}
              dir={dir}
              rows={2}
              value={localeValue}
              onChange={(e) => setValue(e.target.value)}
              className="text-sm"
            />
          ) : (
            <Input
              id={id}
              dir={dir}
              value={localeValue}
              onChange={(e) => setValue(e.target.value)}
              className="text-sm"
            />
          )
        }
      </BilingualTabs>
    </div>
  );
}

// ─── One block row ──────────────────────────────────────────────────────────

function BlockRow({
  pageId,
  block,
  index,
  total,
  busy,
  onMove,
  onToggle,
  onDelete,
}: {
  pageId: string;
  block: BlockSummary;
  index: number;
  total: number;
  busy: boolean;
  onMove: (delta: number) => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  const open = () =>
    void navigate({
      to: '/pages/$id/blocks/$blockId',
      params: { id: pageId, blockId: block.id },
    });

  return (
    <li
      className={`group flex items-center gap-3 rounded-2xl border border-admin-200 bg-white px-4 py-3 transition-colors hover:border-brand-200 ${
        block.isActive ? '' : 'opacity-60'
      }`}
    >
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-admin-100 text-xs font-semibold tabular-nums text-admin-600">
        {index + 1}
      </span>

      <button
        type="button"
        onClick={open}
        className="min-w-0 flex-1 text-start"
        aria-label={`Edit ${blockLabel(block.type)}`}
      >
        <span className="block truncate text-sm font-semibold text-admin-900 group-hover:text-brand-700">
          {block.title ?? blockLabel(block.type)}
        </span>
        <span className="mt-0.5 block truncate font-mono text-xs text-admin-500">{block.type}</span>
      </button>

      {!block.isActive && (
        <span className="shrink-0 rounded-full bg-admin-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-admin-500">
          hidden
        </span>
      )}

      <div className="flex shrink-0 items-center gap-1">
        {/* Arrow reorder rather than drag: @dnd-kit is not a dependency of
            this workspace and CLAUDE.md §7 makes a new third-party package an
            ask-first decision. Arrows are also the keyboard-accessible path a
            drag surface would have had to provide anyway. */}
        <MiniButton
          label={`Move ${blockLabel(block.type)} up`}
          disabled={index === 0 || busy}
          onClick={() => onMove(-1)}
        >
          ↑
        </MiniButton>
        <MiniButton
          label={`Move ${blockLabel(block.type)} down`}
          disabled={index === total - 1 || busy}
          onClick={() => onMove(1)}
        >
          ↓
        </MiniButton>
        <button
          type="button"
          onClick={onToggle}
          className="rounded-lg px-2.5 py-1 text-xs font-medium text-admin-600 transition-colors hover:bg-admin-100"
        >
          {block.isActive ? 'Hide' : 'Show'}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg px-2.5 py-1 text-xs font-medium text-heart transition-colors hover:bg-heart/10"
        >
          Delete
        </button>
      </div>
    </li>
  );
}

function MiniButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-7 w-7 place-items-center rounded-lg text-sm text-admin-500 transition-colors hover:bg-admin-100 disabled:opacity-30"
    >
      {children}
    </button>
  );
}

// ─── Block gallery ──────────────────────────────────────────────────────────

function BlockGalleryDialog({
  pending,
  onPick,
  onClose,
}: {
  pending: boolean;
  onPick: (type: (typeof BLOCK_GALLERY)[number]) => void;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent title="Add a block" className="max-w-3xl bg-white">
        <p className="text-sm text-admin-600">
          Pick a section type. It is added to the bottom of the page and opens straight in the block
          editor.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {BLOCK_GALLERY.map((type) => {
            const schema = BLOCK_SCHEMAS[type];
            return (
              <button
                key={type}
                type="button"
                disabled={pending}
                onClick={() => onPick(type)}
                className="rounded-2xl border border-admin-200 bg-white p-4 text-start transition-colors hover:border-brand-300 hover:bg-brand-50/40 disabled:opacity-50"
              >
                <span className="block text-sm font-semibold text-admin-900">{schema.label}</span>
                <span className="mt-1 block text-xs leading-relaxed text-admin-500">
                  {schema.description}
                </span>
                <span className="mt-2 block font-mono text-[10px] text-admin-400">{type}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Snapshots ──────────────────────────────────────────────────────────────

function SnapshotsPanel({
  pageId,
  onFlash,
  onError,
  onRestored,
}: {
  pageId: string;
  onFlash: (message: string) => void;
  onError: (err: unknown) => void;
  onRestored: () => void;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [confirmRestore, setConfirmRestore] = useState<string | null>(null);

  const snapshots = useQuery({
    queryKey: ['pages', 'snapshots', pageId],
    queryFn: ({ signal }) => listSnapshots(pageId, signal),
    staleTime: 10_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['pages', 'snapshots', pageId] });

  const take = useMutation({
    mutationFn: () => createSnapshot(pageId, label.trim() || null),
    onSuccess: () => {
      setLabel('');
      onFlash('Snapshot taken.');
      void invalidate();
    },
    onError,
  });

  const restore = useMutation({
    mutationFn: (snapshotId: string) => restoreSnapshot(pageId, snapshotId),
    onSuccess: () => {
      setConfirmRestore(null);
      onFlash('Snapshot restored.');
      void invalidate();
      onRestored();
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (snapshotId: string) => deleteSnapshot(pageId, snapshotId),
    onSuccess: () => {
      onFlash('Snapshot deleted.');
      void invalidate();
    },
    onError,
  });

  const rows = snapshots.data ?? [];

  return (
    <section className="mt-8 rounded-2xl border border-admin-200 bg-white p-5">
      <h2 className="text-lg font-semibold text-admin-900">Snapshots</h2>
      <p className="mt-0.5 text-sm text-admin-500">
        Edits go live immediately, so a snapshot is the undo. Restoring one snapshots the current
        state first — you can always step back again.
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <label className="flex-1 text-sm font-semibold text-admin-900">
          Label <span className="font-normal text-admin-500">(optional)</span>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Before the pricing rewrite"
            className="mt-1.5 min-w-56 text-sm"
          />
        </label>
        <Button variant="secondary" onClick={() => take.mutate()} loading={take.isPending}>
          Take snapshot
        </Button>
      </div>

      <div className="mt-4">
        {snapshots.isLoading ? (
          <p className="text-sm text-admin-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="rounded-xl border border-dashed border-admin-200 px-4 py-6 text-center text-sm text-admin-500">
            No snapshots yet.
          </p>
        ) : (
          <ul className="divide-y divide-admin-200/70">
            {rows.map((snap) => (
              <li key={snap.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-admin-900">
                    {snap.label ?? 'Untitled snapshot'}
                  </span>
                  <span className="mt-0.5 block text-xs text-admin-500">
                    {new Date(snap.createdAt).toLocaleString()} · {snap.blockCount} block
                    {snap.blockCount === 1 ? '' : 's'}
                    {snap.createdBy ? ` · ${snap.createdBy}` : ''}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirmRestore(snap.id)}
                  disabled={restore.isPending}
                >
                  Restore
                </Button>
                <button
                  type="button"
                  onClick={() => remove.mutate(snap.id)}
                  disabled={remove.isPending}
                  className="rounded-lg px-2.5 py-1 text-xs font-medium text-heart transition-colors hover:bg-heart/10 disabled:opacity-50"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmRestore && (
        <ConfirmDialog
          title="Restore this snapshot?"
          body="Every block on this page is replaced with the snapshot's. The current state is snapshotted first, so this is reversible."
          confirmLabel="Restore"
          pending={restore.isPending}
          onCancel={() => setConfirmRestore(null)}
          onConfirm={() => restore.mutate(confirmRestore)}
        />
      )}
    </section>
  );
}

// ─── Confirm dialog ─────────────────────────────────────────────────────────

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  pending,
  destructive,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  pending: boolean;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open onOpenChange={(next) => !next && onCancel()}>
      <DialogContent title={title} className="max-w-md bg-white">
        <p className="text-sm leading-relaxed text-admin-600">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            onClick={onConfirm}
            loading={pending}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
